import {
    useState,
    useEffect,
    useRef,
    useContext,
    lazy,
} from '@wordpress/element';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { isOpen, today, getStoreHoursInfo } from '../util';
import { RetailerIdContext } from '../App';
import { ConfigContext } from '..';
import { useRetailerData } from '../hooks/dutchie-queries';
import * as storage from '../browserStorage';
import { storageItemName } from '../config';
const CartSidebar = lazy(() => import('../components/Cart/CartSidebarIcon'));
const CartSidebarIcon = lazy(() => import('../components/Cart/CartSidebarIcon'));
// const ShopNavCategories = lazy(() =>
//     import('../components/Filter/ShopNavCategories')
// );

const ShopNavFull = ({
    shortcodeData: { shortcodeAtts },
    setRetailerId,
    nonHeadlessRetailer
}) => {
    const navigate = useNavigate();
    const params = useParams();

    const {
        showSpecialsButton,
        specialsButtonClass,
        showMenuButton,
        menuButtonClass,
        showSearchbar,
        showLocationbar,
        showAddressbar,
        showCart,
        showCategories,
    } = shortcodeAtts;

    const retailerId = useContext(RetailerIdContext);
    const { data: retailerData, isFetched: isFetchedRetailerData } =
        useRetailerData(retailerId);
    const { retailers, nonHeadlessRetailers, allRetailersConfig } = useContext(ConfigContext);
    const [orderedLocations, setOrderedLocations] = useState([]);

    const [searchQuery, setSearchQuery] = useState('');
    const inputRef = useRef(null);

    // const {
    //     menuConfig: { categoriesToShow },
    // } = useContext(ConfigContext);

    const currentNonHeadlessRetailer = nonHeadlessRetailer;
    const isNonHeadlessRetailer = !!currentNonHeadlessRetailer;

    const getCurrentRetailer = () => {
        if (isNonHeadlessRetailer) {
            const index = nonHeadlessRetailers.findIndex(r => r.location_name === currentNonHeadlessRetailer);
            return index !== -1 ? nonHeadlessRetailers[index] : null;
        }
        return retailers[retailerId] || null;
    };

    const currentRetailer = getCurrentRetailer();

    const renderAddress = () => {
        if (isNonHeadlessRetailer && currentRetailer) {
            return `${currentRetailer.address || ''}, ${currentRetailer.city || ''}, ${currentRetailer.state || ''} ${currentRetailer.zip || ''}`.trim().replace(/^,\s*/, '').replace(/,\s*$/, '');
        }
        if (isFetchedRetailerData && retailerData?.address) {
            return retailerData.address;
        }
        return '';
    };

    const getNonHeadlessRetailerHours = () => {
        if (isNonHeadlessRetailer && currentRetailer && currentRetailer.hours) {
            const todayDay = new Date().toLocaleString('en-us', { weekday: 'long' });
            const todayHours = currentRetailer.hours.find(day => day.day === todayDay);

            if (todayHours) {
                if (todayHours.closed) {
                    return { isClosed: true };
                }

                const now = new Date();
                const [openHour, openMinute] = todayHours.open_time.split(':');
                const [closeHour, closeMinute] = todayHours.close_time.split(':');
                const openTime = new Date(now.setHours(openHour, openMinute, 0));
                const closeTime = new Date(now.setHours(closeHour, closeMinute, 0));

                const isCurrentlyOpen = now >= openTime && now < closeTime;

                return {
                    isOpen: isCurrentlyOpen,
                    openTime: todayHours.open_time,
                    closeTime: todayHours.close_time,
                    isClosed: false
                };
            }
        }
        return null;
    };

    const renderHours = () => {
        if (isNonHeadlessRetailer && currentRetailer) {
            const hoursInfo = getStoreHoursInfo(currentRetailer);

            if (hoursInfo) {
                return (
                    <div className='headless-oc-option'>
                        {hoursInfo.isClosed ? (
                            <span className='headless-oc-closed'>CLOSED</span>
                        ) : hoursInfo.isOpen ? (
                            <span className='headless-oc-open'>
                                OPEN - Closes at {hoursInfo.closeTime}
                            </span>
                        ) : (
                            <span className='headless-oc-closed'>
                                CLOSED - Opens at {hoursInfo.openTime}
                            </span>
                        )}
                    </div>
                );
            }
        } else if (isFetchedRetailerData && retailerData.hours.regular) {
            return (
                <div className='headless-oc-option'>
                    {retailerData?.hours?.regular?.[today()] &&
                        isOpen(retailerData?.hours.regular[today()]) ? (
                        <span className='headless-oc-open'>
                            OPEN - Closes at{' '}
                            {retailerData?.hours.regular[today()].end}
                        </span>
                    ) : (
                        <span className='headless-oc-closed'>
                            CLOSED - Opens at{' '}
                            {retailerData?.hours.regular[today()].start}
                        </span>
                    )}
                </div>
            );
        }
        return null;
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();

        const retailerMenuSlug = retailers[retailerId]?.menuSlug;

        if (retailerMenuSlug && searchQuery) {
            const fullUrl = `${window.location.origin
                }/shop/${retailerMenuSlug}/?search=${encodeURIComponent(
                    searchQuery
                )}`;
            window.location.href = fullUrl;
        }
    };

    const handleDropdownChange = (e) => {
        // Accessing selected option directly from the event
        const selectedOption = e.target.options[e.target.selectedIndex];

        if (!selectedOption) {
            console.error('Selected option not found');
            return;
        }

        const selectedRetailer = selectedOption.getAttribute('data-retailer');
        const selectedNonHeadlessRetailer = selectedOption.getAttribute('data-url');

        // Check if data-retailer attribute is present and not null
        if (!selectedRetailer && !selectedNonHeadlessRetailer) {
            console.error('Retailer data not available on selected option');
            return;
        }

        if (selectedRetailer) {
            const selected = JSON.parse(selectedRetailer);
            storage.removeItem('NON_HEADLESS_RETAILER');

            if (
                params?.viewName === 'shop' && selected?.menuSlug ||
                allRetailersConfig && allRetailersConfig.locationMenuRedirect && selected?.menuSlug
            ) {
                window.location.href = `/shop/${selected.menuSlug}/`;
            } else if (
                selected.locationHomepage
            ) {
                setRetailerId(selected.id);
                window.location.href = selected.locationHomepage;
            } else if (selected.id) {
                setRetailerId(selected.id);
            }
        } else if (selectedNonHeadlessRetailer) {
            const selectedNonHeadlessRetailerData = nonHeadlessRetailers[parseInt(e.target.value.split('-')[2])];
            storage.removeItem(storageItemName);
            storage.setItem('NON_HEADLESS_RETAILER', selectedNonHeadlessRetailerData.location_name);

            if (allRetailersConfig && allRetailersConfig.locationMenuRedirect && selectedNonHeadlessRetailerData.menu_page) {
                window.location.href = selectedNonHeadlessRetailerData.menu_page;
            } else {
                window.location.href = selectedNonHeadlessRetailer;
            }
        }
    };

    const clearSearchInput = () => {
        setSearchQuery('');
        inputRef.current.focus();
    };

    const getSelectedValue = () => {
        const nonHeadlessRetailer = storage.getItem('NON_HEADLESS_RETAILER');

        if (nonHeadlessRetailer) {
            const index = nonHeadlessRetailers.findIndex(r => r.location_name === nonHeadlessRetailer);
            if (index !== -1) {
                return `non-headless-${index}`;
            }
        }

        if (Object.values(retailers).some((retailer) => retailer.id === retailerId)) {
            return retailerId;
        }

        return '-1';
    };

    const selectRef = useRef(null);

    const handleSelectKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            // Remove e.preventDefault()
            // Just let the native behavior happen
        }
    };

    /**
     * Add the search query to the input if it exists in the URL.
     */
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const query = searchParams.get('search');
        if (query) {
            setSearchQuery(decodeURIComponent(query));
        }
    }, [location]);

    /**
     * Get custom location ordering.
     */
    useEffect(() => {
        if (allRetailersConfig?.customRetailersOrder) {
            const allLocations = new Map();

            // Map all retailers and non-headless retailers
            Object.values(retailers).forEach(retailer => {
                allLocations.set(retailer.niceName, retailer);
            });

            nonHeadlessRetailers.forEach((retailer, index) => {
                allLocations.set(retailer.location_name, {
                    ...retailer,
                    isNonHeadless: true,
                    index
                });
            });

            // Create ordered list based on customRetailersOrder
            const ordered = allRetailersConfig.customRetailersOrder
                .map(order => allLocations.get(order.custom_retailers_order_name))
                .filter(Boolean);

            setOrderedLocations(ordered);
        }
    }, [retailers, nonHeadlessRetailers, allRetailersConfig]);

    return (
        <>
            <div
                className={`headless-menu-navbar ${retailers &&
                    retailers.length < 2 &&
                    params?.viewName === 'shop'
                    ? 'headless-menu-navbar-single-location'
                    : ''
                    }`}
            >
                <div
                    className={`headless-menu-navbar-wrapper ${params?.viewName === 'shop'
                        ? 'menu-navbar-menu-page'
                        : ''
                        }`}
                >
                    {retailers &&
                        Object.keys(retailers).length > 1 &&
                        showLocationbar ? (
                        <div
                            className={`headless-menu-navbar-select ${retailers &&
                                Object.keys(retailers).length > 1 &&
                                showLocationbar
                                ? ''
                                : 'hidden'
                                }`}
                        >
                            {
                                currentRetailer && currentRetailer.locationImage ? (
                                    <div
                                        className='headless-lns-image'
                                        style={{ backgroundImage: `url(${currentRetailer.locationImage})` }}
                                    ></div>
                                ) : (
                                    <div className='headless-lns-icon'>
                                        <svg
                                            xmlns='http://www.w3.org/2000/svg'
                                            viewBox='0 0 576 512'
                                        >
                                            <path d='M507.1 129.5l0 0c5.8 9.2 6.4 20.5 2.3 30.1c-3.9 9.2-11.1 14.8-20.1 16c-2 .3-3.9 .4-5.8 .4c-11.7 0-22.2-5.1-29.7-13.2c-9.1-10-22-15.7-35.6-15.7s-26.5 5.8-35.5 15.8c-7.3 8.1-17.7 13.2-29.6 13.2c-11.8 0-22.3-5.1-29.6-13.2c-9.1-10.1-22-15.8-35.6-15.8s-26.5 5.7-35.6 15.8c-7.3 8.1-17.7 13.2-29.6 13.2c-11.8 0-22.3-5.1-29.6-13.2c-9.1-10.1-22-15.8-35.6-15.8s-26.5 5.7-35.6 15.8c-7.3 8.1-17.7 13.2-29.6 13.2c-1.8 0-3.8-.1-5.8-.4c-8.9-1.2-16-6.8-19.9-16c-4.1-9.6-3.5-20.9 2.3-30.1l0 0 0 0L120.4 48H455.6l51.5 81.5zM483.5 224c4.1 0 8.1-.3 12-.8c55.5-7.4 81.8-72.5 52.1-119.4L490.3 13.1C485.2 5 476.1 0 466.4 0H109.6C99.9 0 90.8 5 85.7 13.1L28.3 103.8c-29.6 46.8-3.4 111.9 51.9 119.4c4 .5 8.1 .8 12.1 .8c0 0 0 0 0 0c19.6 0 37.5-6.4 51.9-17c4.8-3.5 9.2-7.6 13.2-11.9c4 4.4 8.4 8.4 13.2 11.9c14.5 10.6 32.4 17 52 17c19.6 0 37.5-6.4 52-17c4.8-3.5 9.2-7.6 13.2-12c4 4.4 8.4 8.4 13.2 11.9c14.5 10.6 32.4 17 52 17c19.8 0 37.8-6.5 52.3-17.3c4.7-3.5 9-7.4 12.9-11.7c3.9 4.3 8.3 8.3 13 11.8c14.5 10.7 32.5 17.2 52.2 17.2c0 0 0 0 0 0zM112 336V254.4c-6.4 1.1-12.9 1.6-19.6 1.6c-5.5 0-11-.4-16.3-1.1l-.1 0c-4.1-.6-8.1-1.3-12-2.3V336v48 64c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V384 336 252.6c-4 1-8 1.8-12.3 2.3l-.1 0c-5.3 .7-10.7 1.1-16.2 1.1c-6.6 0-13.1-.5-19.4-1.6V336H112zm352 48v64c0 8.8-7.2 16-16 16H128c-8.8 0-16-7.2-16-16V384H464z'></path>
                                        </svg>
                                    </div>
                                )
                            }

                            <div className='headless-lns-select'>
                                <div className='headless-lns-text'>
                                    Currently shopping
                                </div>
                                <select
                                    ref={selectRef}
                                    className='form-select'
                                    id='msoLocationDropdown'
                                    value={getSelectedValue()}
                                    onChange={handleDropdownChange}
                                    aria-label='Select shopping location'
                                    onKeyDown={handleSelectKeyDown}
                                >
                                    <option value='-1'>Where are you shopping?</option>
                                    {orderedLocations.length > 0 ? (
                                        orderedLocations.map((location) => (
                                            location.isNonHeadless ? (
                                                <option
                                                    key={`non-headless-${location.index}`}
                                                    value={`non-headless-${location.index}`}
                                                    data-url={location.non_headless_location_homepage}
                                                    role='option'
                                                    aria-selected={getSelectedValue() === `non-headless-${location.index}`}
                                                >
                                                    {location.location_name}
                                                </option>
                                            ) : (
                                                <option
                                                    key={location.id}
                                                    value={location.id}
                                                    data-retailer={JSON.stringify(location)}
                                                    id={`option-${location.id}`}
                                                    aria-selected={getSelectedValue() === location.id}
                                                >
                                                    {location.niceName || location.retailerName}
                                                </option>
                                            )
                                        ))
                                    ) : (
                                        // Original rendering logic as fallback
                                        <>
                                            {Object.values(retailers).map((retailer) => (
                                                <option
                                                    key={retailer.id}
                                                    value={retailer.id}
                                                    data-retailer={JSON.stringify(retailer)}
                                                    id={`option-${retailer.id}`}
                                                    aria-selected={getSelectedValue() === retailer.id}
                                                >
                                                    {retailer.niceName ? retailer.niceName : retailer.retailerName}
                                                </option>
                                            ))}
                                            {nonHeadlessRetailers.length > 0 && (
                                                <>
                                                    {nonHeadlessRetailers.map((retailer, index) => (
                                                        <option
                                                            key={`non-headless-${index}`}
                                                            value={`non-headless-${index}`}
                                                            data-url={retailer.non_headless_location_homepage}
                                                            role='option'
                                                            aria-selected={getSelectedValue() === `non-headless-${index}`}
                                                        >
                                                            {retailer.location_name}
                                                        </option>
                                                    ))}
                                                </>
                                            )}
                                        </>
                                    )}
                                </select>
                            </div>
                        </div>
                    ) : null}
                    {showAddressbar && (
                        <div className='headless-menu-navbar-info'>
                            <div className='headless-menu-navbar-address'>
                                {renderAddress()}
                            </div>
                            {renderHours()}
                        </div>
                    )}
                    {showSearchbar && !isNonHeadlessRetailer && (
                        <form
                            className={`headless-menu-navbar-searchbar-form ${params?.viewName === 'shop'
                                ? 'headless-menu-navbar-searchbar-form-menu'
                                : 'headless-menu-navbar-searchbar-form-page'
                                }`}
                            onSubmit={handleSearchSubmit}
                        >
                            <div
                                className={`headless-searchbar headless-menu-navbar-searchbar ${params?.viewName === 'shop'
                                    ? 'menu-navbar-menu-page-search'
                                    : ''
                                    }`}
                            >
                                <input
                                    ref={inputRef}
                                    placeholder='Search for products...'
                                    className='headless-nav-searchbar-input headless-menu-navbar-searchbar-input'
                                    type='text'
                                    aria-label='Search for products'
                                    value={searchQuery}
                                    onChange={(e) =>
                                        setSearchQuery(e.target.value)
                                    }
                                />
                                {searchQuery && (
                                    <div
                                        className='clear-search-icon'
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            clearSearchInput();
                                        }}
                                        aria-label='clear search'
                                    >
                                        <svg
                                            xmlns='http://www.w3.org/2000/svg'
                                            viewBox='0 0 512 512'
                                        >
                                            <path d='M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm97.9-320l-17 17-47 47 47 47 17 17L320 353.9l-17-17-47-47-47 47-17 17L158.1 320l17-17 47-47-47-47-17-17L192 158.1l17 17 47 47 47-47 17-17L353.9 192z' />
                                        </svg>
                                    </div>
                                )}
                            </div>
                            <button
                                type='submit'
                                className='headless-button headless-menu-navbar-searchbar-submit'
                            >
                                Go
                            </button>
                        </form>
                    )}
                    {showCategories && (
                        <div className='headless-menu-navbar-categories'>
                            {/* <ShopNavCategories
                                filters={categoriesToShow}
                                menuSlug={menuSlug}
                            /> */}
                        </div>
                    )}
                    {params?.viewName !== 'shop' && (
                        <div className='headless-menu-navbar-buttons'>
                            {showSpecialsButton && !isNonHeadlessRetailer && (
                                <div className='headless-menu-navbar-specials'>
                                    <Link
                                        to={`/shop/${retailers[retailerId]?.menuSlug}/specials`}
                                        reloadDocument
                                        className={`${specialsButtonClass
                                            ? specialsButtonClass +
                                            ' headless-menu-navbar-btn headless-specials-btn'
                                            : 'headless-button headless-specials-btn'
                                            }`}
                                    >
                                        <span>
                                            <span className='headless-menu-navbar-specials-shop'>
                                                Shop{' '}
                                            </span>
                                            Specials
                                        </span>
                                    </Link>
                                </div>
                            )}
                            {showMenuButton && (
                                <div
                                    className={`headless-menu-navbar-shop ${!showSpecialsButton
                                        ? 'headless-menu-navbar-shop-full'
                                        : ''
                                        }`}
                                >
                                    <Link
                                        to={isNonHeadlessRetailer ? currentRetailer.menu_page : `/shop/${retailers[retailerId]?.menuSlug}/`}
                                        reloadDocument
                                        id='headless-shopnav-btn'
                                        className={`${menuButtonClass
                                            ? menuButtonClass +
                                            ' headless-menu-navbar-btn headless-menunav-btn'
                                            : 'headless-button headless-menunav-btn'
                                            }`}
                                    >
                                        <span>
                                            Order
                                            <span className='headless-menu-navbar-order-shop'>
                                                {' '}
                                                Online
                                            </span>
                                        </span>
                                    </Link>
                                </div>
                            )}
                            {params?.viewName !== 'shop' && params?.viewName !== 'cart' && showCart && !isNonHeadlessRetailer && (
                                <div className='headless-menu-navbar-cart'>
                                    <CartSidebar />
                                </div>
                            )}
                        </div>
                    )}
                    {params?.viewName === 'shop' && params?.viewName !== 'cart' && showCart && !isNonHeadlessRetailer && (
                        <div className='headless-menu-navbar-cart'>
                            <CartSidebarIcon />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default ShopNavFull;
